import { CONTRACTOR_TYPE } from "src/stores/OrderStore";
import {
    HISTORY_TYPE,
    INVOICE_ACCOUNT_TYPE,
    INVOICE_ACCOUNT_UNIT_OF_MEASURE,
    INVOICE_ACCOUNT_VAT_CONFIG,
    INVOICE_FILE_STATE,
    INVOICE_FILE_TYPE,
    INVOICE_LIST_STATE,
    INPUTWORKFLOW_FILE_STATE,
    INVOICE_STATE,
    INVOICE_TYPE,
    SORT,
    VAT_RATE_TYPE,
    WORKFLOWUSER_ROLE,
    INPUTWORKFLOW_STATE,
    INVOICE_ORIGIN
} from "./InvoiceEnums";
import { cred_invoices_insert_input } from "src/api/generated/globalTypes";
import { IFile } from "src/types";

export interface IAssignedUser {
    id: string;
    name1: string;
    name2?: string | null;
}

export interface IRealestate {
    id: string;
    number: string;
    name: string;
    zip: string;
    city: string;
    consolidation?: boolean;
    consolidationId?: string;
}

export interface IUnitData {
    houseId: string;
    unitId: string;
    unitNumber?: number;
    unitName: string;
    tenancyData?: ITenancyData;
    tenantName?: string;
    isVacant?: boolean;
}

export interface ITenancyData {
    tenancyid?: string;
    tenantid?: string;
}

export interface ICreditor {
    id: string;
    name: string; // From the creditor (cred.creditors)
    number?: number | null;
    personId?: string;
    name1?: string | null; // From the person (common.persons) - Is normally identical to the creditor name
    name2?: string | null; // From the person (common.persons) - Is used as the name suffix (Namenszusatz)
    mobile?: string | null;
    phonebusiness?: string | null;
    email?: string | null;
    gender?: number | null;
    street?: string | null;
    housenumber?: string | null;
    zip?: string | null;
    city?: string | null;
    origin?: number | null;
    paymentAccounts?: IPaymentAccount[];
}

export interface IInvoicePaymentInfo {
    iban?: string;
    type?: string;
    reference?: string;
}

export interface ICreditorInfo {
    name?: string;
    number?: number | null;
    street?: string;
    housenumber?: string;
    zip?: string;
    city?: string;
    country?: string;
    uid?: string;
}

export interface IInvoiceInfo {
    vatrate?: number;
    vatdate?: string;
    conditions?: IInvoiceInfoConditions;
}

export interface IInvoiceType {
    name: string;
    type: INVOICE_TYPE;
}

export interface IHistoryChangeEntry {
    description?: string;
    oldValue?: string;
    newValue?: any;
}

export interface IHistoryEmailAttachment {
    fileId: string;
    fileName: string;
}

export interface IHistoryEmailLog {
    emailLogId?: string;
    recipient?: string;
    event?: string;
    message?: string;
}

export interface IHistoryEmail {
    subject?: string;
    body?: string;
    sender?: string;
    recipients?: string;
    type?: number;
    attachments?: IHistoryEmailAttachment[];
    sendingErrors?: IHistoryEmailLog[];
}
export interface IHistory {
    type?: HISTORY_TYPE;
    date?: string;
    timestamp?: Date;
    changeText?: string;
    user?: string;
    changeEntry?: IHistoryChangeEntry[];
    email?: IHistoryEmail;
    entityid?: string;
    isPinned: boolean;
}

export interface IInvoiceProperties {
    id?: string;
    origin?: number;
    workflowInstance?: string;
    number?: number;
    invoiceNumber?: string;
    amount?: number;
    date?: string;
    dueDate?: string;
    paidDate?: string;
    state?: INVOICE_STATE;
    invoiceType?: INVOICE_TYPE;
    realestate?: IRealestate;
    realestateId?: string;
    realestateThumbnailFileId?: string;
    unit?: IUnitData;
    assignedUser?: IAssignedUser | null;
    creditor?: ICreditor;
    paymentaccountid?: string;
    payoutaccountid?: string;
    paymentInfo?: IInvoicePaymentInfo;
    paymentAccount?: IPaymentAccount;
    history?: IHistory[];
    ticket?: ITicket;
    order?: IOrder;
    invoiceFile?: IInvoiceFile;
    invoiceAttachmentFiles?: IFile[];
    aiDetection?: IAiDetection;
    creditorInfo?: ICreditorInfo;
    insertUserId?: string;
    invoiceInfo?: IInvoiceInfo;
    comment?: string;
    workflowUsers?: IWorkflowUser[];
    accountings?: IAccounting[];
    duplicates?: IInvoiceDuplicate[];
    numberOfVisa?: number;
    isProcessing?: boolean;
    paymentPurpose?: string;
}

export interface IInvoiceFilter {
    state: INVOICE_LIST_STATE;
    sort: SORT;
    query: string;
    types: INVOICE_TYPE[];
    showOnlyMyInvoices: boolean;
    showOnlyInvoicesFromMyRealestates: boolean;
}

export interface ISearchTag {
    label: string;
    id: string;
    active: boolean;
    type?: INVOICE_TYPE;
}

export interface IPaymentAccount {
    id?: string;
    iban?: string;
    name?: string;
    type?: number;
    valid?: boolean;
}

export interface ITicket {
    id?: string;
    realestateid?: string;
    unitid?: string;
    date?: string; // 01.07.2024
    date_long?: string; // 1. Juli 2024
    number?: string;
    title?: string;
}

export interface IOrder {
    id?: string;
    number?: string;
    realestateid?: string;
    date?: string; // 01.07.2024
    date_long?: string; // 1. Juli 2024
    contractorId?: string;
    title?: string;
    instruction?: string;
    contractorName1?: string;
    contractorName2?: string;
    ticket?: ITicket;
    contractorType?: CONTRACTOR_TYPE;
}

export interface IInvoiceFile {
    invoiceFileId?: string;
    fileId?: string;
    name?: string;
    url?: string;
    type?: INVOICE_FILE_TYPE;
    base64String?: string;
    mimetype?: string;
    extension?: string;
    fileDate?: string;
    state?: INVOICE_FILE_STATE;
    size?: number;
}

export interface IInputWorkflowUploadFile extends IInvoiceFile {
    size: number;
    fileObject: File;
}

// TODO Remove once InputWorkflow is implemented
export interface IInvoiceStagingFile {
    id?: string;
    fileId?: string;
    name?: string;
    url?: string;
    type?: INVOICE_FILE_TYPE;
    base64String?: string;
    mimetype?: string;
    extension?: string;
    fileDate?: string;
    state?: INPUTWORKFLOW_FILE_STATE;
    emailId?: string;
    groupId?: string;
    previousFileId?: string;
    workflowId?: string;
}

export interface IInputWorkflow {
    id: string;
    emailId?: string;
    origin?: INVOICE_ORIGIN;
    state?: INPUTWORKFLOW_STATE;
    workflowInstance?: string;
    inputWorkflowFiles?: IInputWorkflowFile[];
    uiName?: string; // this is a temporary name until the inputworkflow was created in the backend
}

export interface IInputWorkflowFile {
    id?: string;
    fileId?: string;
    name?: string;
    url?: string;
    type?: INVOICE_FILE_TYPE;
    base64String?: string;
    mimetype?: string;
    extension?: string;
    fileDate?: string;
    inputWorkflowId?: string;
    state?: INPUTWORKFLOW_FILE_STATE;
    parentFileId?: string;
    level?: number;
    realestateId?: string;
    realestate?: IRealestate; // Not in DB but helper to store information about the realestate
}

export interface ISplitPdfFile {
    fileId?: string;
    name?: string;
    url?: string;
}

export interface IInvoiceUpsertVariables extends cred_invoices_insert_input {
    file?: IInvoicePDFInsertVariables;
    existingfileid?: string;
    existingfile?: IExistingFileInsertVariables;
}

export interface IInvoicePDFInsertVariables {
    invoicepdf: string; // base64 string of the pdf
    name: string;
}

export interface IExistingFileInsertVariables {
    name: string;
    extension: string;
    mimetype: string;
    hash: string; // MD5 from /ai-detect-with-file response, passed through 1:1
}

export interface ICreateInvoiceResponse {
    data: {
        invoiceId: string;
        workflowInstanceId: string;
        fileId: string;
        number: number;
    };
}

export interface IWorkflowUserDetails {
    id: string;
    name1: string;
    name2: string;
}

export interface IWorkflowUser {
    id: string;
    role: WORKFLOWUSER_ROLE;
    signaturestamp: string;
    user: IWorkflowUserDetails;
}

export interface IActiveRoleState {
    role: WORKFLOWUSER_ROLE;
    state: INVOICE_STATE;
}

export const activeRoleStates: IActiveRoleState[] = [
    { role: WORKFLOWUSER_ROLE.NONE, state: INVOICE_STATE.NEW },
    { role: WORKFLOWUSER_ROLE.NONE, state: INVOICE_STATE.ASSIGN_REALESTATE },
    { role: WORKFLOWUSER_ROLE.NONE, state: INVOICE_STATE.RESTART_AFTER_REJECTED },
    { role: WORKFLOWUSER_ROLE.NONE, state: INVOICE_STATE.ASSIGN_WORKFLOW_USERS },
    { role: WORKFLOWUSER_ROLE.VISA_1, state: INVOICE_STATE.READY_FOR_VISA },
    { role: WORKFLOWUSER_ROLE.VISA_2, state: INVOICE_STATE.VISA_1_OK },
    { role: WORKFLOWUSER_ROLE.VISA_3, state: INVOICE_STATE.VISA_2_OK },
    { role: WORKFLOWUSER_ROLE.ACCOUNTANT, state: INVOICE_STATE.VISA_3_OK },
    { role: WORKFLOWUSER_ROLE.ACCOUNTANT, state: INVOICE_STATE.FULLY_SIGHTED },
    { role: WORKFLOWUSER_ROLE.ACCOUNTANT, state: INVOICE_STATE.SENT_TO_ERP },
    { role: WORKFLOWUSER_ROLE.ACCOUNTANT, state: INVOICE_STATE.IMPORTED_IN_ERP },
    { role: WORKFLOWUSER_ROLE.ACCOUNTANT, state: INVOICE_STATE.PAID },
    { role: WORKFLOWUSER_ROLE.ACCOUNTANT, state: INVOICE_STATE.REJECTED },
    { role: WORKFLOWUSER_ROLE.NONE, state: INVOICE_STATE.CANCELLED }
];

export interface IAccounting {
    id?: string;
    amount?: string; // Betrag (inkl. MwSt.)
    date?: string;
    realestate?: IRealestate; // Liegenschaft / Mandant
    unit?: IUnitData; // Objekt
    account?: IAccount;
    costCenter?: ICostCenter;
    isMissingRequiredCostCenterData?: boolean;
    text?: string;
    quantity?: string;
    extraCostDate?: string;
    netAmount?: string; // Betrag (exkl. MwSt.)
    vatAmount?: string;
    vatRate?: string;
    vatCodeId?: string;
    vatDate?: string;
}

export interface IAccountingReadOnly extends IAccounting {
    isTotalRow?: boolean;
    totalAmount?: string;
}

export interface IAccountingValidationError {
    isDateMissing?: boolean;
    isRealestateMissing?: boolean;
    isAccountMissing?: boolean;
    isCostCenterMissing?: boolean;
    isBookingTextMissing?: boolean;
    isAmountMissing?: boolean;
}
export interface IAccount {
    id?: string;
    name?: string;
    number?: string;
    type?: INVOICE_ACCOUNT_TYPE;
    validFrom: string;
    validTo?: string;
    realestate?: IRealestate;
    realestateId?: string;
    tenant?: {}; // TODO: Define ITenant Interface once needed
    requiresCostCenter?: boolean;
    requiresQuantity?: boolean;
    unitOfMeasure?: INVOICE_ACCOUNT_UNIT_OF_MEASURE;
    vatConfig?: INVOICE_ACCOUNT_VAT_CONFIG;
    defaultVatCodeId?: string;
    extraCostDate?: boolean;
}

export interface IAccountTag {
    accountId: string;
    number?: string;
    name?: string;
    accountingsHistoryText?: string;
    numberOfRecords?: number;
}

export interface ICostCenter {
    id?: string;
    name?: string;
    short?: string;
}

export interface IPayoutAccountData {
    id?: string;
    iban?: string;
    name?: string;
    valid?: boolean;
    default?: boolean;
}

// Types for QR extrated data
export interface IQRExtractInvoiceData {
    invoicenumber: string | null;
    date: string | null;
    duedate: string | null;
    paiddate: string | null;
    amount: number;
    creditorid: string | null;
    paymentaccountid: string | null;
    payoutaccountid: string | null;
    paymentinfo: IInvoicePaymentInfo;
    realestateid: string | null;
    incidentid: string | null;
    orderid: string | null;
    unitid: string | null;
    creditorinfo: ICreditorInfo;
    invoiceinfo: IInvoiceInfo;
}

export interface IInvoiceInfoConditions {
    discountRates: IInvoiceInfoDiscountRate[] | null;
    ZahlungsfristTage: number | null;
}

export interface IInvoiceInfoDiscountRate {
    precentage: number | null;
    days: number | null;
}

export interface IVatData {
    realestateId: string | null;
    language?: string;
    rateId?: string;
    rate?: string;
    rateName?: string; // v_vatcodes_vatrates.vatrate_resolved - Ist das Label aus den Enumerations
    codeId?: string;
    codeName?: string;
    codeShort?: string;
    validFrom: string;
    validTo?: string;
    vatRateType?: VAT_RATE_TYPE;
}

export interface INumberOfVisas {
    realestateId: string | null;
    numberOfVisa: number;
}

export interface IRealestateVisaRealestate {
    id: string;
    number: string;
    name: string;
    zip: string;
    city: string;
}

export interface IRealestateVisaVisa {
    id?: string;
    realestateId: string;
    role: number;
    userId: string;
    userName1?: string;
    userName2?: string;
}

export interface IRealestateVisa {
    realestate: IRealestateVisaRealestate;
    visas: IRealestateVisaVisa[];
}

export interface IInvoiceDuplicate {
    invoiceId: string;
    invoiceNumber: number;
    amount: number;
}

export interface IAccountingsHistoryTag {
    creditorId: string;
    accountNumber: string;
    text: string;
    system: boolean;
}

export interface IAccountingsHistoryByAccountNumber {
    creditorName: string;
    accountNumber: string;
    text: string;
}

// Types for AI detection (POST /api/krediflow/ai-detect-with-file)
export type AiDetectionOutcome = "match" | "ambiguous" | "no_match" | "no_query";
export type AiDetectionState = "pending" | "completed" | "failed";

export interface IAiDetectionCandidatePayload {
    // realestate fields (field="realestateid")
    realestate_name?: string;
    realestate_number?: string;
    owner_full_name?: string;
    // creditor fields (field="creditorid")
    creditor_name?: string;
    creditor_number?: string;
    iban?: string;
    // shared address fields
    street?: string;
    housenumber?: string;
    zip?: string;
    city?: string;
    [key: string]: unknown;
}

export interface IAiDetectionCandidate {
    id: string; // candidate-row PK — used for MarkDetectionUserSource (acceptedcandidateid)
    rank: number; // 0 = best
    entityid: string; // realestate.id / creditor.id — used for createinvoice.realestateid / .creditorid
    displayname: string;
    payload: IAiDetectionCandidatePayload;
}

export interface IAiFieldDetection {
    detectionid: string;
    outcome: AiDetectionOutcome;
    candidateCount: number;
    candidates: IAiDetectionCandidate[];
    // Per-field accepted state, mirroring the cred_invoice_ai_detections row.
    // This is the only place it lives: both the realestate and the creditor
    // flow read and write it here, per field.
    acceptedsource?: AiDetectionSource | null;
    acceptedcandidateid?: string | null;
    // Pre-save UI flag: true between the moment the frontend auto-applied this
    // field's match candidate and the next user-driven change to the field.
    // Distinct from acceptedsource='ai_auto', which means the server-side
    // conductor flow auto-applied.
    wasAutoAppliedByAi?: boolean;
}

// Invoice header data the AI extracts alongside the realestate detection
// (POST /api/krediflow/ai-detect-with-file -> data.document). Used as a fallback
// to fill the fields the Swiss QR code often lacks. Dates are ISO strings
// ("yyyy-MM-dd"); amounts are numbers; creditor/debtor are free text (no entity id).
// Mirrors the backend AiDocumentDetails / AiContactAddress (micro-ai !9764 shape):
// absent fields arrive as null, never "".
export interface IAiContactAddress {
    name?: string | null;
    // Street without house number.
    street?: string | null;
    housenumber?: string | null;
    // Postfach / c/o line.
    street_2?: string | null;
    postal_code?: string | null;
    city?: string | null;
    email?: string | null;
}

export interface IAiRealEstateInfo {
    name?: string | null;
    owner?: string | null;
    street?: string | null;
    housenumber?: string | null;
    postal_code?: string | null;
    city?: string | null;
    unit?: string | null;
    area?: string | null;
}

export interface IAiDocumentDetails {
    title?: string;
    invoice_number?: string;
    invoice_date?: string | null;
    invoice_due_date?: string | null;
    customer_number?: string;
    order_number?: string;
    creditor?: IAiContactAddress | null;
    debtor?: IAiContactAddress | null;
    realestate?: IAiRealEstateInfo | null;
    total_amount?: number | null;
}

export interface IAiDocumentExtraction {
    document_type: string; // "invoice" | "credit_note" | "other"
    details: IAiDocumentDetails | null;
}

export interface IAiDetectWithFileResponse {
    data: {
        fileid: string;
        filename: string;
        extension: string;
        mimetype: string;
        hash: string;
        sizeBytes: number;
        // A field is absent when its lookup failed on the AI side.
        results: {
            realestateid?: IAiFieldDetection;
            creditorid?: IAiFieldDetection;
        };
        document?: IAiDocumentExtraction | null;
    };
}

export type AiDetectionSource = "ai_auto" | "user";

// Metadata needed by the new-invoice flow to thread fileid + hash into the
// createinvoice call (existingfileid path). Absent for detections loaded from
// the database — the original /ai-detect-with-file response only exists in
// memory at the moment of the manual upload.
export interface IAiDetectionUploadMetadata {
    fileid: string;
    filename: string;
    extension: string;
    mimetype: string;
    hash: string;
}

// Stored on the Invoice. Either built client-side from the /ai-detect-with-file
// response (new-invoice flow, uploadMetadata present) or hydrated from the
// cred.invoice_ai_detections DB row when loading an existing invoice
// (conductor-flow invoices, uploadMetadata absent).
export interface IAiDetection {
    state: AiDetectionState;
    // A field is absent when its lookup failed on the AI side. All per-field
    // state (outcome, candidates, acceptedsource, wasAutoAppliedByAi) lives on
    // the field entry — there is no top-level mirror of any one field.
    results: {
        realestateid?: IAiFieldDetection;
        creditorid?: IAiFieldDetection;
    };
    uploadMetadata?: IAiDetectionUploadMetadata;
}
